import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Users,
  AlertTriangle,
  CheckCircle,
  XCircle,
  RefreshCw,
  Zap,
  UserX,
  UserCheck,
  Building2,
  Calculator,
  PiggyBank,
  Target,
  Shield,
  Heart,
  Clock,
  ChevronRight,
  ArrowUpRight,
  ArrowDownRight,
  Activity,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function PredictiveFeatures() {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('resignation');
  const [loading, setLoading] = useState(false);
  const [resignationData, setResignationData] = useState(null);
  const [revenueData, setRevenueData] = useState(null);
  const [quickFixing, setQuickFixing] = useState(false);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('qapi-token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }, []);

  const fetchResignationRisk = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/careflow/predictive/resignation-risk`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setResignationData(data);
      }
    } catch (error) {
      console.error('Error fetching resignation risk:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRevenueImpact = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/careflow/predictive/revenue-impact`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setRevenueData(data);
      }
    } catch (error) {
      console.error('Error fetching revenue impact:', error);
    } finally {
      setLoading(false);
    }
  };

  const runQuickFixAll = async () => {
    setQuickFixing(true);
    try {
      const response = await fetch(`${API_URL}/api/careflow/predictive/quick-fix-all`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const result = await response.json();
        toast({
          title: result.overall_status === 'success' ? 'All Issues Fixed!' : 'Partial Fix Applied',
          description: result.message,
          variant: result.overall_status === 'success' ? 'default' : 'warning',
        });
        // Refresh data
        fetchResignationRisk();
        fetchRevenueImpact();
      } else {
        toast({
          title: 'Fix Failed',
          description: 'Unable to apply quick fixes',
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Network error applying fixes',
        variant: 'destructive',
      });
    } finally {
      setQuickFixing(false);
    }
  };

  useEffect(() => {
    fetchResignationRisk();
    fetchRevenueImpact();
  }, []);

  const getRiskColor = (level) => {
    switch (level) {
      case 'Critical': return 'bg-red-500';
      case 'High': return 'bg-orange-500';
      case 'Medium': return 'bg-yellow-500';
      case 'Low': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  const getRiskBadge = (level) => {
    switch (level) {
      case 'Critical': return 'destructive';
      case 'High': return 'destructive';
      case 'Medium': return 'warning';
      case 'Low': return 'default';
      default: return 'secondary';
    }
  };

  return (
    <div className="space-y-6" data-testid="predictive-features">
      {/* Header with Quick Fix All */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Zap className="w-6 h-6 text-amber-500" />
            Advanced Predictive Analytics
          </h2>
          <p className="text-muted-foreground">AI-powered insights for workforce and financial planning</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => { fetchResignationRisk(); fetchRevenueImpact(); }}
            disabled={loading}
            data-testid="refresh-predictive-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            onClick={runQuickFixAll}
            disabled={quickFixing}
            className="gap-2 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
            data-testid="quick-fix-all-btn"
          >
            {quickFixing ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Fixing...
              </>
            ) : (
              <>
                <Zap className="w-4 h-4" />
                Quick Fix All Data Issues
              </>
            )}
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-white/50 dark:bg-slate-800/50">
          <TabsTrigger value="resignation" className="gap-2" data-testid="tab-resignation">
            <UserX className="w-4 h-4" />
            Resignation Risk
          </TabsTrigger>
          <TabsTrigger value="revenue" className="gap-2" data-testid="tab-revenue">
            <DollarSign className="w-4 h-4" />
            Revenue Impact
          </TabsTrigger>
        </TabsList>

        {/* Resignation Risk Tab */}
        <TabsContent value="resignation" className="space-y-4">
          {resignationData ? (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <Card className="bg-gradient-to-br from-slate-50 to-gray-100 dark:from-slate-800 dark:to-slate-900">
                  <CardContent className="p-4 text-center">
                    <Users className="w-8 h-8 mx-auto mb-2 text-blue-500" />
                    <p className="text-2xl font-bold">{resignationData.summary?.total_nurses || 0}</p>
                    <p className="text-xs text-muted-foreground">Total Nurses</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-red-50 to-rose-100 dark:from-red-900/30 dark:to-rose-900/30 border-red-200">
                  <CardContent className="p-4 text-center">
                    <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-red-500" />
                    <p className="text-2xl font-bold text-red-600">{resignationData.summary?.critical_risk || 0}</p>
                    <p className="text-xs text-muted-foreground">Critical Risk</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-orange-50 to-amber-100 dark:from-orange-900/30 dark:to-amber-900/30 border-orange-200">
                  <CardContent className="p-4 text-center">
                    <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-orange-500" />
                    <p className="text-2xl font-bold text-orange-600">{resignationData.summary?.high_risk || 0}</p>
                    <p className="text-xs text-muted-foreground">High Risk</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-yellow-50 to-amber-100 dark:from-yellow-900/30 dark:to-amber-900/30 border-yellow-200">
                  <CardContent className="p-4 text-center">
                    <Clock className="w-8 h-8 mx-auto mb-2 text-yellow-500" />
                    <p className="text-2xl font-bold text-yellow-600">{resignationData.summary?.medium_risk || 0}</p>
                    <p className="text-xs text-muted-foreground">Medium Risk</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-green-50 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30 border-green-200">
                  <CardContent className="p-4 text-center">
                    <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500" />
                    <p className="text-2xl font-bold text-green-600">{resignationData.summary?.low_risk || 0}</p>
                    <p className="text-xs text-muted-foreground">Low Risk</p>
                  </CardContent>
                </Card>
              </div>

              {/* Financial Impact Alert */}
              {resignationData.financial_impact?.high_risk_count > 0 && (
                <Alert className="bg-gradient-to-r from-rose-50 to-red-50 dark:from-rose-900/20 dark:to-red-900/20 border-rose-200">
                  <DollarSign className="h-5 w-5 text-rose-500" />
                  <AlertTitle className="text-rose-700">Potential Turnover Cost</AlertTitle>
                  <AlertDescription className="text-rose-600">
                    <span className="font-bold text-2xl">${resignationData.financial_impact?.potential_turnover_cost?.toLocaleString()}</span>
                    {' '}at risk if {resignationData.financial_impact?.high_risk_count} high-risk nurses leave. 
                    {' '}{resignationData.financial_impact?.roi_of_retention}
                  </AlertDescription>
                </Alert>
              )}

              {/* Recommendations */}
              {resignationData.recommendations?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Target className="w-5 h-5 text-violet-500" />
                      Retention Recommendations
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {resignationData.recommendations.map((rec, idx) => (
                        <div key={idx} className="flex items-start gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                          <Badge variant={rec.priority === 'critical' ? 'destructive' : rec.priority === 'high' ? 'warning' : 'secondary'}>
                            {rec.priority}
                          </Badge>
                          <div className="flex-1">
                            <p className="font-medium">{rec.action}</p>
                            <p className="text-sm text-muted-foreground">{rec.detail}</p>
                            {rec.estimated_savings && (
                              <p className="text-sm text-emerald-600 mt-1">Potential savings: {rec.estimated_savings}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Risk Factors Analyzed Banner */}
              {resignationData.risk_factors_analyzed && (
                <Card className="bg-gradient-to-r from-slate-50 to-gray-100 dark:from-slate-800 dark:to-slate-900">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Shield className="w-4 h-4 text-violet-500" />
                      Risk Factors Analyzed
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="flex flex-wrap gap-2">
                      {resignationData.risk_factors_analyzed.map((factor, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          {factor}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Nurse Risk Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-500" />
                    Individual Risk Assessment
                  </CardTitle>
                  <CardDescription>Nurses sorted by resignation risk score - includes enhanced workload metrics</CardDescription>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[500px]">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Nurse</TableHead>
                          <TableHead>Team</TableHead>
                          <TableHead>Risk Score</TableHead>
                          <TableHead>Risk Level</TableHead>
                          <TableHead>Caseload</TableHead>
                          <TableHead>Acuity</TableHead>
                          <TableHead>Travel</TableHead>
                          <TableHead>Doc %</TableHead>
                          <TableHead>Key Factors</TableHead>
                          <TableHead>Interventions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {resignationData.risk_analysis?.map((nurse, idx) => (
                          <TableRow key={idx} className={nurse.risk_level === 'Critical' ? 'bg-red-50 dark:bg-red-900/20' : nurse.risk_level === 'High' ? 'bg-orange-50 dark:bg-orange-900/10' : ''}>
                            <TableCell className="font-medium">{nurse.nurse_name}</TableCell>
                            <TableCell>
                              <Badge variant="outline">{nurse.team}</Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Progress value={nurse.risk_score} className="w-16 h-2" />
                                <span className="text-sm font-medium">{nurse.risk_score}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant={getRiskBadge(nurse.risk_level)}>
                                {nurse.risk_level}
                              </Badge>
                            </TableCell>
                            <TableCell>{nurse.caseload || '-'}</TableCell>
                            <TableCell>{nurse.enhanced_metrics?.avg_acuity || '-'}</TableCell>
                            <TableCell>
                              {nurse.enhanced_metrics?.travel_time_avg_min 
                                ? `${nurse.enhanced_metrics.travel_time_avg_min}m` 
                                : nurse.enhanced_metrics?.daily_travel_miles 
                                  ? `${nurse.enhanced_metrics.daily_travel_miles}mi` 
                                  : '-'}
                            </TableCell>
                            <TableCell>
                              {nurse.enhanced_metrics?.documentation_compliance 
                                ? `${nurse.enhanced_metrics.documentation_compliance}%` 
                                : '-'}
                            </TableCell>
                            <TableCell>
                              <div className="space-y-1 max-w-xs">
                                {nurse.risk_factors?.slice(0, 2).map((factor, i) => (
                                  <p key={i} className="text-xs text-muted-foreground">{factor.factor}</p>
                                ))}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="space-y-1">
                                {nurse.interventions?.slice(0, 2).map((int, i) => (
                                  <Badge key={i} variant="outline" className="text-xs block">
                                    {int.action}
                                  </Badge>
                                ))}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                </CardContent>
              </Card>

              {/* Cost Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PiggyBank className="w-5 h-5 text-emerald-500" />
                    Turnover Cost Breakdown
                  </CardTitle>
                  <CardDescription>Average cost to replace one nurse: ${resignationData.financial_impact?.estimated_turnover_cost_per_nurse?.toLocaleString()}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-4 gap-4">
                    {Object.entries(resignationData.financial_impact?.cost_breakdown || {}).map(([key, value]) => (
                      <div key={key} className="text-center p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                        <p className="text-xl font-bold">${value.toLocaleString()}</p>
                        <p className="text-xs text-muted-foreground capitalize">{key.replace('_', ' ')}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <div className="flex items-center justify-center h-64">
              <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          )}
        </TabsContent>

        {/* Revenue Impact Tab */}
        <TabsContent value="revenue" className="space-y-4">
          {revenueData ? (
            <>
              {/* Current State Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <Users className="w-6 h-6 text-blue-500" />
                      <Badge variant={revenueData.current_state?.staffing_gap > 0 ? 'destructive' : 'default'}>
                        {revenueData.current_state?.staffing_gap > 0 ? `${revenueData.current_state?.staffing_gap} short` : 'Adequate'}
                      </Badge>
                    </div>
                    <p className="text-3xl font-bold">{revenueData.current_state?.nurse_count}</p>
                    <p className="text-xs text-muted-foreground">Current Nurses</p>
                    <p className="text-sm text-blue-600 mt-1">Optimal: {revenueData.current_state?.optimal_nurse_count}</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-emerald-50 to-green-100 dark:from-emerald-900/30 dark:to-green-900/30">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <DollarSign className="w-6 h-6 text-emerald-500" />
                      <TrendingUp className="w-5 h-5 text-emerald-500" />
                    </div>
                    <p className="text-3xl font-bold">${(revenueData.financial_metrics?.annual_revenue / 1000000)?.toFixed(1)}M</p>
                    <p className="text-xs text-muted-foreground">Annual Revenue</p>
                    <p className="text-sm text-emerald-600 mt-1">${revenueData.financial_metrics?.monthly_revenue?.toLocaleString()}/mo</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-violet-50 to-purple-100 dark:from-violet-900/30 dark:to-purple-900/30">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <UserCheck className="w-6 h-6 text-violet-500" />
                      <Badge variant="secondary">${revenueData.financial_metrics?.revenue_per_patient_day}/day</Badge>
                    </div>
                    <p className="text-3xl font-bold">${(revenueData.financial_metrics?.revenue_per_nurse / 1000)?.toFixed(0)}K</p>
                    <p className="text-xs text-muted-foreground">Revenue per Nurse</p>
                    <p className="text-sm text-violet-600 mt-1">Profit: ${(revenueData.financial_metrics?.profit_margin_per_nurse / 1000)?.toFixed(0)}K/nurse</p>
                  </CardContent>
                </Card>
                <Card className={`bg-gradient-to-br ${revenueData.current_state?.capacity_utilization > 100 ? 'from-red-50 to-rose-100 dark:from-red-900/30 dark:to-rose-900/30' : 'from-amber-50 to-yellow-100 dark:from-amber-900/30 dark:to-yellow-900/30'}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <Activity className="w-6 h-6 text-amber-500" />
                      <Badge variant={revenueData.current_state?.capacity_utilization > 100 ? 'destructive' : 'warning'}>
                        {revenueData.current_state?.capacity_utilization > 100 ? 'Overstretched' : 'Monitor'}
                      </Badge>
                    </div>
                    <p className="text-3xl font-bold">{revenueData.current_state?.capacity_utilization?.toFixed(0)}%</p>
                    <p className="text-xs text-muted-foreground">Capacity Utilization</p>
                    <p className="text-sm text-amber-600 mt-1">Avg caseload: {revenueData.current_state?.avg_caseload}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Understaffing Risk Alert */}
              {revenueData.understaffing_risk?.level !== 'low' && (
                <Alert className={`bg-gradient-to-r ${revenueData.understaffing_risk?.level === 'critical' ? 'from-red-50 to-rose-50 border-red-200' : 'from-amber-50 to-yellow-50 border-amber-200'}`}>
                  <AlertTriangle className={`h-5 w-5 ${revenueData.understaffing_risk?.level === 'critical' ? 'text-red-500' : 'text-amber-500'}`} />
                  <AlertTitle>Understaffing Risk: {revenueData.understaffing_risk?.level?.toUpperCase()}</AlertTitle>
                  <AlertDescription>
                    <div className="flex flex-wrap gap-4 mt-2">
                      <span>Overtime cost: <strong>${revenueData.understaffing_risk?.overtime_cost_monthly?.toLocaleString()}/mo</strong></span>
                      <span>Burnout risk: <strong>{revenueData.understaffing_risk?.burnout_turnover_risk}</strong></span>
                      <span>Quality impact: <strong>{revenueData.understaffing_risk?.quality_impact}</strong></span>
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              {/* Hiring ROI Calculator */}
              {revenueData.hiring_roi?.recommended_hires > 0 && (
                <Card className="bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border-emerald-200">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Calculator className="w-5 h-5 text-emerald-500" />
                      Hiring ROI Calculator
                    </CardTitle>
                    <CardDescription>Financial impact of recommended hiring</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {/* Investment */}
                      <div className="space-y-3">
                        <h4 className="font-semibold text-rose-600 flex items-center gap-2">
                          <ArrowDownRight className="w-4 h-4" />
                          Investment Required
                        </h4>
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span>Recommended Hires</span>
                            <span className="font-bold">{revenueData.hiring_roi?.recommended_hires}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span>Recruiting</span>
                            <span>${revenueData.hiring_roi?.breakdown?.recruiting?.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span>Training</span>
                            <span>${revenueData.hiring_roi?.breakdown?.training?.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span>First Year Salary</span>
                            <span>${revenueData.hiring_roi?.breakdown?.first_year_salary?.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between font-bold pt-2 border-t">
                            <span>Total Investment</span>
                            <span className="text-rose-600">${revenueData.hiring_roi?.total_hiring_cost?.toLocaleString()}</span>
                          </div>
                        </div>
                      </div>

                      {/* Benefits */}
                      <div className="space-y-3">
                        <h4 className="font-semibold text-emerald-600 flex items-center gap-2">
                          <ArrowUpRight className="w-4 h-4" />
                          Expected Benefits
                        </h4>
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span>Additional Capacity</span>
                            <span className="font-bold">{revenueData.hiring_roi?.expected_benefits?.additional_capacity}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span>Potential Revenue</span>
                            <span>${revenueData.hiring_roi?.expected_benefits?.potential_revenue?.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span>Avoided Turnover</span>
                            <span>${revenueData.hiring_roi?.expected_benefits?.avoided_turnover_cost?.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span>Avoided Overtime</span>
                            <span>${revenueData.hiring_roi?.expected_benefits?.avoided_overtime?.toLocaleString()}</span>
                          </div>
                        </div>
                      </div>

                      {/* ROI */}
                      <div className="space-y-3 bg-white/50 dark:bg-slate-800/50 rounded-lg p-4">
                        <h4 className="font-semibold text-violet-600 flex items-center gap-2">
                          <TrendingUp className="w-4 h-4" />
                          ROI Analysis
                        </h4>
                        <div className="space-y-4">
                          <div className="text-center">
                            <p className="text-4xl font-bold text-emerald-600">{revenueData.hiring_roi?.roi_analysis?.roi_percentage}%</p>
                            <p className="text-sm text-muted-foreground">First Year ROI</p>
                          </div>
                          <div className="text-center">
                            <p className="text-2xl font-bold">${revenueData.hiring_roi?.roi_analysis?.net_roi_year_1?.toLocaleString()}</p>
                            <p className="text-sm text-muted-foreground">Net Return Year 1</p>
                          </div>
                          <div className="text-center">
                            <p className="text-xl font-bold">{revenueData.hiring_roi?.roi_analysis?.payback_period_months} months</p>
                            <p className="text-sm text-muted-foreground">Payback Period</p>
                          </div>
                          <Badge className="w-full justify-center py-2" variant={revenueData.hiring_roi?.recommendation === 'Strongly recommended' ? 'default' : 'secondary'}>
                            {revenueData.hiring_roi?.recommendation}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Cost Structure */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-slate-500" />
                    Cost Structure
                  </CardTitle>
                  <CardDescription>Per-nurse cost breakdown</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-5 gap-4">
                    <div className="text-center p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <p className="text-xl font-bold">${revenueData.cost_structure?.avg_nurse_salary?.toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground">Base Salary</p>
                    </div>
                    <div className="text-center p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <p className="text-xl font-bold">${revenueData.cost_structure?.benefits_cost?.toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground">Benefits</p>
                    </div>
                    <div className="text-center p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <p className="text-xl font-bold">${revenueData.cost_structure?.total_cost_per_nurse?.toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground">Total Cost</p>
                    </div>
                    <div className="text-center p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <p className="text-xl font-bold">${revenueData.cost_structure?.recruiting_cost?.toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground">Recruiting</p>
                    </div>
                    <div className="text-center p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <p className="text-xl font-bold">${revenueData.cost_structure?.training_cost?.toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground">Training</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Growth Scenarios */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-blue-500" />
                    Census Growth Scenarios
                  </CardTitle>
                  <CardDescription>Financial projections for different growth rates</CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Growth</TableHead>
                        <TableHead>Projected Census</TableHead>
                        <TableHead>Nurses Needed</TableHead>
                        <TableHead>Additional Hires</TableHead>
                        <TableHead>Hiring Cost</TableHead>
                        <TableHead>Projected Revenue</TableHead>
                        <TableHead>Revenue Increase</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {revenueData.growth_scenarios?.map((scenario, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-bold">+{scenario.growth_percent}%</TableCell>
                          <TableCell>{scenario.projected_census}</TableCell>
                          <TableCell>{scenario.nurses_needed}</TableCell>
                          <TableCell>
                            <Badge variant={scenario.additional_hires > 0 ? 'warning' : 'default'}>
                              {scenario.additional_hires > 0 ? `+${scenario.additional_hires}` : 'None'}
                            </Badge>
                          </TableCell>
                          <TableCell>${scenario.hiring_cost?.toLocaleString()}</TableCell>
                          <TableCell className="font-medium">${(scenario.projected_annual_revenue / 1000000)?.toFixed(2)}M</TableCell>
                          <TableCell className="text-emerald-600">+${(scenario.revenue_increase / 1000)?.toFixed(0)}K</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Key Insights */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="w-5 h-5 text-amber-500" />
                    Key Insights
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {revenueData.key_insights?.map((insight, idx) => (
                      <div key={idx} className="flex items-start gap-2 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                        <ChevronRight className="w-5 h-5 text-amber-500 mt-0.5" />
                        <p>{insight}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <div className="flex items-center justify-center h-64">
              <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
